'use client';

import { incrementTelemetry } from 'core-events/actions';
import Link from 'next/link';
import { useState, useEffect, JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import { Toast, TabsRoot, TabsList, TabsContent } from '@videoblocks/design-system';

import toCamelCase from '../../../utils/camelCase';
import { Tabs, useVoiceoverTabContext } from './_context/VoiceoverTabContext';
import CloneVoiceToSpeech from './_features/CloneVoiceToSpeech';
import TextToSpeech from './_features/TextToSpeech';
import VoiceChanger from './_features/VoiceChanger';

const tabs = [
    { label: 'Text to Speech', path: 'text-to-speech' },
    { label: 'Clone my Voice', path: 'clone-my-voice' },
    { label: 'Voice Changer', path: 'voice-changer' },
];

const VoiceoverContainer = ({ active }: { active: string }): JSX.Element => {
    const [error, setError] = useState<string | undefined>(undefined);
    const { setActiveTab } = useVoiceoverTabContext();

    useEffect(() => {
        // TODO: when setting the active tab. the tabs content seems to see the old state first before the new state
        setActiveTab(toCamelCase(active) as Tabs);
    }, [active, setActiveTab]);

    const activeTab = tabs.find((t) => t.path === active) ?? tabs[0];

    return (
        <>
            <TabsRoot defaultValue={activeTab.label}>
                <div className="flex justify-center mb-6">
                    <TabsList className="bg-gray-800 rounded-lg p-1 flex">
                        {tabs.map((tab) => (
                            <Link
                                key={tab.path}
                                href={`/voiceover/${tab.path}`}
                                className={twMerge(
                                    'px-4 py-3 rounded-md text-sm font-medium cursor-pointer',
                                    activeTab === tab && 'bg-gray-900 font-semibold rounded-md',
                                )}
                                onClick={(e) => {
                                    void incrementTelemetry(`ai.voiceover.tabClick.${toCamelCase(tab.path)}`);
                                }}
                            >
                                {tab.label}
                            </Link>
                        ))}
                    </TabsList>
                </div>
                <TabsContent value="Text to Speech">
                    <TextToSpeech />
                </TabsContent>
                <TabsContent value="Clone my Voice">
                    <CloneVoiceToSpeech setError={setError} />
                </TabsContent>
                <TabsContent value="Voice Changer">
                    <VoiceChanger />
                </TabsContent>
            </TabsRoot>
            <Toast
                open={!!error}
                onOpenChange={() => setError(undefined)}
                duration={5000}
                title={error}
                variant="error"
                viewportClassName="fixed bottom-[var(--spacing-12)] left-1/2 transform -translate-x-1/2 flex flex-col gap-[var(--spacing-2)] w-fit max-w-[calc(100vw-var(--spacing-12))] m-0 list-none z-[1050] outline-none"
            />
        </>
    );
};

export default VoiceoverContainer;
